import {Button, Input, InputNumber} from 'antd'
import {course_createFeeType_Api, course_deleteFeeType_Api, course_editFeeType_Api, course_getFeeTypes_Api,} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useState} from 'react'
import {PiInfoFill, PiPlusBold, PiTrash} from 'react-icons/pi'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import LoadingBlock from 'views/Loading/LoadingBlock'
import formatCurrency from '../../../Billing/components/formatCurrency'

const money = (value) => formatCurrency(value ?? 0)

// Số minh hoạ cho mô tả hướng dẫn phí tín chỉ khi CHƯA khai (quick-add, chưa có đơn giá thật) - khớp
// đơn giá mẫu 150.000đ/tín chỉ đã thống nhất khi phân tích tính năng này.
const EXAMPLE_CREDIT_UNIT_PRICE = 150000
const EXAMPLE_CREDIT_COUNT = 8

// Danh mục type_code chuẩn - MIRROR App\Services\CourseFinance\CourseFeeTypeCatalog::DEFAULT_TYPES
// (BE, app/Services/CourseFinance/CourseFeeTypeCatalog.php) - đổi 1 bên nhớ đổi bên còn lại. Dùng để
// gợi ý "Thêm nhanh" các loại phí chuẩn thay vì bắt gõ tay từng cái - CREDIT có mô tả riêng vì cách
// tính khác hẳn (nhân theo tín chỉ, không phải phí phẳng/đầu học viên).
const FEE_TYPE_CATALOG = [
    {type_code: 'CREDIT', name: 'Học phí tín chỉ (ECTS)', is_per_credit: true, hintKey: 'credit fee type hint'},
    {type_code: 'REGISTRATION', name: 'Phí đăng ký & quản lý', is_per_credit: false},
    {type_code: 'DEVELOPMENT', name: 'Phí phát triển', is_per_credit: false},
    {type_code: 'STUDENT_SERVICES', name: 'Phí dịch vụ sinh viên', is_per_credit: false},
    {type_code: 'LIBRARY', name: 'Phí thư viện', is_per_credit: false},
    {type_code: 'FACILITY', name: 'Phụ phí cơ sở vật chất', is_per_credit: false},
]

// Bảng "Quy Định Định Mức" (course_fee_types) - dùng chung ở 2 nơi: tab Tài Chính & Học Phí
// (CourseFinanceDashboard.js, xem trong FullSideModalLayout) VÀ ngay trong form Cấu hình Khóa học
// (CourseSetForm.js, hiện khi fee_structure_type = itemized) - 1 nguồn UI duy nhất cho add/sửa/xoá
// định mức, không viết lại 2 lần.
const FeeTypeRow = ({item, can_edit, onChanged}) => {
    const t = useTranslate()
    const [unitPrice, setUnitPrice] = useState(item.unit_price)

    const onError = notifyOnError(t)
    const {handleAsyncAction: editFeeType, isLoading: saving} = useAsyncAction({
        apiInfo: course_editFeeType_Api,
        onError,
        onSuccess: onChanged,
    })
    const {handleAsyncAction: deleteFeeType, isLoading: deleting} = useAsyncAction({
        apiInfo: course_deleteFeeType_Api,
        onError,
        onSuccess: onChanged,
    })

    const dirty = Number(unitPrice) !== Number(item.unit_price)
    const catalogHint = FEE_TYPE_CATALOG.find((c) => c.type_code === item.type_code)?.hintKey

    return (
        <div className="flex flex-col gap-1 py-2 border-b border-color-50 last:border-b-0">
            <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm text-color-000 truncate">{item.name}</span>
                    {item.is_per_credit && (
                        <span className="ml-2 text-xs font-medium text-indigo-500">{`(${t('per credit')})`}</span>
                    )}
                </div>
                {can_edit ? (
                <>
                    <InputNumber
                        min={0}
                        step={1000}
                        value={unitPrice}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                        parser={(v) => v.replace(/\./g, '')}
                        onChange={setUnitPrice}
                        className="w-32"
                    />
                    {dirty && (
                        <Button
                            size="small"
                            type="primary"
                            loading={saving}
                            onClick={() => editFeeType({unit_price: unitPrice}, {id: getId(item)})}>
                            {t('save')}
                        </Button>
                    )}
                    {item.type_code !== 'CREDIT' && (
                        <Button
                            size="small"
                            danger
                            type="text"
                            loading={deleting}
                            icon={<PiTrash size={13}/>}
                            onClick={() => showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete fee type',
                                message: 'are you sure you want to delete this fee type',
                                onOk: () => deleteFeeType({}, {id: getId(item)}),
                            })}
                        />
                    )}
                </>
                ) : (
                    <span className="font-bold text-sm text-color-000">{money(item.unit_price)}</span>
                )}
            </div>
            {/* Mô tả hướng dẫn riêng cho phí theo tín chỉ - giải thích rõ cách tính kèm ví dụ cụ thể
                theo số tín chỉ, tránh admin nhập đơn giá mà không hiểu vì sao thành tiền lại nhân lên. */}
            {catalogHint && (
                <div className="ml-0 flex items-start gap-1.5 text-xs text-color-400 italic tracking-wide">
                    <PiInfoFill size={13} className="mt-0.5 flex-shrink-0 text-indigo-400"/>
                    <span>{t(catalogHint, {
                        price: money(unitPrice),
                        count: EXAMPLE_CREDIT_COUNT,
                        example: money((Number(unitPrice) || 0) * EXAMPLE_CREDIT_COUNT),
                    })}</span>
                </div>
            )}
        </div>
    )
}

const AddFeeTypeForm = ({course_id, onAdded}) => {
    const t = useTranslate()
    const [name, setName] = useState('')
    const [unitPrice, setUnitPrice] = useState(0)

    const onError = notifyOnError(t)
    const {handleAsyncAction: createFeeType, isLoading} = useAsyncAction({
        apiInfo: course_createFeeType_Api,
        onError,
        onSuccess: (result) => {
            setName('')
            setUnitPrice(0)
            onAdded(result)
        },
    })

    return (
        <div className="flex items-center gap-2 pt-3 mt-1 border-t border-color-100">
            <Input
                placeholder={t('new fee type name placeholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1"
            />
            <InputNumber min={0} step={1000} value={unitPrice} onChange={setUnitPrice} className="w-32"/>
            <Button
                type="dashed"
                icon={<PiPlusBold size={12}/>}
                loading={isLoading}
                disabled={!name.trim()}
                onClick={() => createFeeType({name: name.trim(), unit_price: unitPrice}, {id: course_id})}>
                {t('add')}
            </Button>
        </div>
    )
}

// "Thêm nhanh" các loại phí CHUẨN (FEE_TYPE_CATALOG) chưa được khai cho khóa này - hiện dưới dạng
// chip bấm 1 phát ra luôn (unit_price=0, vào sửa số sau ở FeeTypeRow), KHÔNG bắt gõ tay lại tên như
// AddFeeTypeForm (chỉ dùng cho loại phí thật sự ngoài danh mục). CREDIT có caption riêng giải thích
// cách tính ngay dưới chip, để admin hiểu trước khi bấm thêm.
const QuickAddCatalog = ({course_id, existingTypeCodes, onAdded}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)
    const [addingCode, setAddingCode] = useState(null)

    const {handleAsyncAction: createFeeType} = useAsyncAction({
        apiInfo: course_createFeeType_Api,
        onError,
        onSuccess: (result) => {
            setAddingCode(null)
            onAdded(result)
        },
    })

    const missing = FEE_TYPE_CATALOG.filter((c) => !existingTypeCodes.includes(c.type_code))
    if (missing.length === 0) return null

    return (
        <div className="mb-3 pb-3 border-b border-color-100 flex flex-col gap-2">
            <span className="text-xs font-semibold text-color-300 uppercase tracking-wider">{t('quick add standard fee types')}</span>
            <div className="flex flex-wrap gap-2">
                {missing.map((c) => (
                    <Button
                        key={c.type_code}
                        size="small"
                        icon={<PiPlusBold size={11}/>}
                        loading={addingCode === c.type_code}
                        onClick={() => {
                            setAddingCode(c.type_code)
                            createFeeType({
                                name: c.name,
                                type_code: c.type_code,
                                unit_price: 0,
                                is_per_credit: c.is_per_credit,
                            }, {id: course_id})
                        }}>
                        {c.name}
                    </Button>
                ))}
            </div>
            {missing.some((c) => c.type_code === 'CREDIT') && (
                <div className="flex items-start gap-1.5 text-xs text-color-400 italic tracking-wide">
                    <PiInfoFill size={13} className="mt-0.5 flex-shrink-0 text-indigo-400"/>
                    <span>{t('credit fee type hint', {
                        price: money(EXAMPLE_CREDIT_UNIT_PRICE),
                        count: EXAMPLE_CREDIT_COUNT,
                        example: money(EXAMPLE_CREDIT_UNIT_PRICE * EXAMPLE_CREDIT_COUNT),
                    })}</span>
                </div>
            )}
        </div>
    )
}

// Khối nội dung THUẦN (không bọc Modal/Drawer) - list định mức hiện có (đủ chi tiết: tên + đơn giá +
// gắn nhãn "theo tín chỉ" nếu có) kèm action sửa/xoá từng dòng, + form thêm mới ở cuối. Nơi gọi tự
// quyết định bọc trong Drawer (CourseFinanceDashboard.js) hay nhúng thẳng inline (CourseSetForm.js).
export const FeeTypesEditor = ({course_id, can_edit}) => {
    const t = useTranslate()
    const [items, setItems] = useState(null)

    const {handleAsyncAction: fetchFeeTypes, isLoading} = useAsyncAction({
        apiInfo: course_getFeeTypes_Api,
        onSuccess: (result, data) => setItems(_.get(data, 'response.data.data', [])),
    })

    React.useEffect(() => {
        if (course_id) fetchFeeTypes({}, {id: course_id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_id])

    const refresh = useCallback(() => fetchFeeTypes({}, {id: course_id}), [fetchFeeTypes, course_id])

    if (isLoading || items === null) return <LoadingBlock/>

    const existingTypeCodes = items.map((item) => item.type_code)

    return (
        <div className="p-3 border border-color-100 rounded-xl">
            {can_edit && (
                <QuickAddCatalog course_id={course_id} existingTypeCodes={existingTypeCodes} onAdded={refresh}/>
            )}
            {items.length === 0 ? (
                <div className="py-4 text-center text-sm text-color-400 italic">
                    {can_edit ? t('no fee types yet hint') : t('no data')}
                </div>
            ) : (
                items.map((item) => (
                    <FeeTypeRow key={item.id} item={item} can_edit={can_edit} onChanged={refresh}/>
                ))
            )}
            {can_edit && <AddFeeTypeForm course_id={course_id} onAdded={refresh}/>}
        </div>
    )
}

export default FeeTypesEditor
